import { ChangeDetectionStrategy, Component, Signal, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { PackageDb } from 'lib/cosmos/package/packageDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { formulaHelper } from 'lib/helpers/formula-helper.class.js';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { IconType } from 'lib/ui/icons';
import { ListItem } from 'lib/ui/list';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { match } from 'ts-pattern';

@Component({
	selector: 'mod-client-overview',
	templateUrl: './client-overview.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientOverviewComponent extends TypeCoreComponent<UserDb.IUserItem> {
	public readonly form = {
		privateNotes: signal<string | undefined>(undefined),
		publicNotes: signal<string | undefined>(undefined)
	};

	public readonly clientOverviewButtons: Signal<IButton[]> = computed<IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		return [
			{
				title: `Edit profile`,
				buttonType: 'basic',
				icon: 'edit',
				routerLink: super
					.actionRoute((routes) => routes.trainerClientRoutes.client.children.profile, sharedData.data.id)
					.getUrl()
			}
		];
	});

	public readonly demoUserSimpleTable: Signal<SimpleTableItem[]> = computed(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		const tableItems: SimpleTableItem[] = [
			{
				title: `E-mail`,
				value: sharedData.data.email,
				valueType: 'text'
			},
			{
				title: `Password`,
				value: sharedData.data.plainTextPassword,
				valueType: 'text'
			}
		];

		return tableItems;
	});

	private readonly assignedPackages = computed<PackageDb.IPackageItem[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		const packages: PackageDb.IPackageItem[] = [];

		for (const assignedPackage of sharedData.data.assignedPackages ?? []) {
			const linkedPackage = super.getLinkedItemById<PackageDb.IPackageItem>(assignedPackage.packageId);

			if (linkedPackage) {
				packages.push(linkedPackage);
			}
		}

		return packages;
	});

	public readonly assignedPackageListItems = computed<ListItem<PackageDb.IPackageItem>[]>(() => {
		return this.assignedPackages().map((assignedPackage) => {
			const item: ListItem<PackageDb.IPackageItem> = {
				data: assignedPackage,
				id: assignedPackage.id,
				title: assignedPackage.system.name,
				subtitle: assignedPackage.description,
				avatar: assignedPackage.avatar?.[0]?.image
			};

			return item;
		});
	});

	public readonly assignedPackageButtons = computed<IButton[]>(() => {
		return [
			{
				title: 'Manage packages',
				icon: 'manage',
				buttonType: 'primaryLight',
				routerLink: super
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.packages,
						this.sharedData()?.data.id
					)
					.getUrl()
			}
		];
	});

	public readonly clientTableItems = computed<readonly SimpleTableItem[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		const tableItems: readonly SimpleTableItem[] = [
			{
				title: `E-mail`,
				value: sharedData.data.email,
				valueType: 'text',
				iconType: 'email'
			},
			{
				title: `E-mail confirmed`,
				value: sharedData.data.emailConfirmed ? 'yes' : 'no',
				valueType: 'text',
				iconType: 'emailConfirm'
			},
			{
				title: `First name`,
				value: sharedData.data.firstName,
				valueType: 'text',
				iconType: 'user_pen'
			},
			{
				title: `Last name`,
				value: sharedData.data.lastName,
				valueType: 'text',
				iconType: 'user_pen'
			},
			{
				title: `Gender`,
				value: sharedData.data.gender,
				iconType: match(sharedData.data.gender)
					.returnType<IconType>()
					.with('male', () => 'genderMale')
					.with('female', () => 'genderFemale')
					.otherwise(() => 'genderLess'),
				valueType: 'text'
			},
			{
				title: `Location`,
				value: sharedData.data.location,
				iconType: 'map',
				valueType: 'text'
			},
			{
				title: `Birthdate`,
				value: sharedData.data.birthDate
					? `${sharedData.data.birthDate} (${this.timeService.getAge(sharedData.data.birthDate)})`
					: undefined,
				iconType: 'birthdate',
				valueType: 'text',
				hide: sharedData.data.birthDate ? false : true
			},
			{
				title: `Preferred unit`,
				value: sharedData.data.preferredUnit,
				iconType: 'calculator',
				valueType: 'text'
			},
			{
				title: `Weight`,
				value: unitConversionHelper.weightInPreferredUnit(
					this.authUser()?.preferredUnit,
					sharedData.data.weightKg
				),
				iconType: 'weight',
				valueType: 'text'
			},
			{
				title: `Height`,
				value: unitConversionHelper.heightInPreferredUnit(
					this.authUser()?.preferredUnit,
					sharedData.data.heightCm
				),
				iconType: 'height',
				valueType: 'text'
			},
			{
				title: `BMI`,
				value:
					sharedData.data.weightKg && sharedData.data.heightCm
						? formulaHelper.calculateBmi({
								heightInCm: sharedData.data.heightCm,
								weightInKg: sharedData.data.weightKg
						  })
						: '',
				iconType: 'bmi',
				valueType: 'text'
			}
		];

		return tableItems;
	});

	public readonly savePrivateNotesHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return this.dependencies.httpService
			.post<UserDb.IUserItem, UserApi.IEditUserNotesRequest>((builder) =>
				builder
					.withCustomApiRoute('user', 'editPrivateNotes')
					.withId(sharedData.data.id)
					.withData({
						notes: this.form.privateNotes()
					})
					.build()
			)
			.pipe(
				map((response) => {
					super.notifySaved('Notes');
					super.updateData(response.data);
				})
			);
	});

	public readonly savePublicNotesHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return this.dependencies.httpService
			.post<UserDb.IUserItem, UserApi.IEditUserNotesRequest>((builder) =>
				builder
					.withCustomApiRoute('user', 'editPublicNotes')
					.withId(sharedData.data.id)
					.withData({
						notes: this.form.publicNotes()
					})
					.build()
			)
			.pipe(
				map((response) => {
					super.notifySaved('Notes');
					super.updateData(response.data);
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.privateNotes.set(sharedData?.data.privateNotes);
			this.form.publicNotes.set(sharedData?.data.publicNotes);
		};
	}
}
